import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@heroui/react";
import { supabase } from "@/lib/supabase";
import { notifyError } from "@/lib/toast";

interface UpdateRecipeLogNoteVars {
    logId: number;
    recipeId: number;
    note: string;
}

/**
 * Save the note on a log entry. A note edit doesn't change made-status, so this
 * only invalidates that recipe's log list — not the broader recipe caches.
 */
export function useUpdateRecipeLogNote() {
    const queryClient = useQueryClient();

    return useMutation<void, Error, UpdateRecipeLogNoteVars>({
        mutationFn: async ({ logId, note }) => {
            const trimmed = note.trim();
            const { error } = await supabase
                .from("recipe_logs")
                .update({ note: trimmed.length > 0 ? trimmed : null })
                .eq("id", logId);

            if (error) throw error;
        },
        onError: () => {
            notifyError("Your note wasn't saved. Try again.");
        },
        onSuccess: () => {
            toast.success("Note saved", { timeout: 2000 });
        },
        onSettled: (_data, _err, { recipeId }) => {
            queryClient.invalidateQueries({ queryKey: ["recipeLogs", recipeId] });
        }
    });
}
